import { defineStore } from 'pinia';
import useEnvStore, { type Env } from '@/store/modules/env';
import api from '@/utils/api';

// NOTE: how to use this mixin add the below to computed section of where the mixin is imported
// useInternalShipping: {
//   get() {
//     this.getSetting('useInternalShipping') || false;
//   },
//   set(newValue) {
//     this.saveSetting('useInternalShipping', newValue);
//   },
// },

export type Setting = {
  key: string;
  value: unknown;
};

export function findSettingValue<T>(config: Setting[], key: string, fallback: T): T {
  return (config.find((s) => s.key === key)?.value ?? fallback) as T;
}

type State = {
  settingsLoading: boolean;
  settingsData: Setting[];
};

export default defineStore('settings', {
  state: (): State => ({
    settingsLoading: true,
    settingsData: [],
  }),
  getters: {
    getSetting:
      (state) =>
      <T = string>(key: string): T | undefined =>
        state.settingsData.find((s) => s.key === key)?.value as T | undefined,
  },
  actions: {
    async loadSettings(envData?: Env) {
      this.settingsLoading = true;

      const envStore = useEnvStore();

      this.settingsData = [];
      const res = await api.sparkApi<Setting[]>({
        method: 'GET',
        url: '/v1/settings',
        body: {},
      });
      this.settingsData = res.data;

      const env = envData ?? envStore.env;

      if (env?.store_id) {
        const shopifyRes = await api.get(
          `/${env.team.slugged_name}/${env.environment}/shopify/settings`,
        );
        this.settingsData.push(...shopifyRes.data);
      }

      this.settingsLoading = false;
    },
    saveSetting(key: string, value: unknown) {
      this.saveSettings([{ key, value }]);
    },
    async saveSettings(settings: Setting[]) {
      if (this.settingsLoading) {
        return;
      }
      this.settingsLoading = true;

      const envStore = useEnvStore();

      const shopifySettings = [];
      const generalSettings = [];

      for (const { key, value } of settings) {
        // Shopify specific settings use a different endpoint
        if (
          [
            'draftOrdersAutoComplete',
            'draftOrdersAutoCompleteAccountSetAsPaid',
            'draftOrdersAutoCompleteInvoiceSetAsPaid',
            'draftOrdersPriceOverrideEnabled',
            'createDraftOrderOnCalculate',
            'disableNewCustomerInvitation',
            'ignoreTransactions',
            'shippingAutoSelectionStrategy',
          ].includes(key)
        ) {
          shopifySettings.push({ key, value });
        } else {
          generalSettings.push({ key, value });
        }
      }

      if (shopifySettings.length && envStore.env?.platform.startsWith('shopify')) {
        await api.patch(
          `/${envStore.env?.team.slugged_name}/${envStore.env?.environment}/shopify/settings`,
          shopifySettings,
        );
      }

      if (generalSettings.length) {
        await api.sparkApi({
          method: 'PATCH',
          url: '/v1/settings',
          body: generalSettings,
        });
      }

      this.loadSettings();
    },
    async saveBaseGroupSetting(key: string, value: unknown) {
      if (this.settingsLoading) {
        return;
      }
      this.settingsLoading = true;

      await api.sparkApi({
        method: 'PATCH',
        url: '/v1/settings/{slug}',
        params: { slug: 'base' },
        body: [
          {
            key,
            value,
          },
        ],
      });

      this.loadSettings();
    },
  },
});
